/**
 * 패널 맵 vector 피처 인덱스 — String/Array/Module 단위 선택·해제 O(1) 조회
 *
 * ※ String 식별은 stringSeq(DB PK) 기준. stringDisplayNo(Array 내 번호) 사용 금지.
 */
import { readArraySeq, readModuleDisplayNo, readModuleSeq, readStringSeq } from './panelMapFeatureProps.js';

export function createPanelMapFeatureIndex() {
  return {
    byStringSeq: new Map(),
    byArrayId: new Map(),
    /** @deprecated 표시 번호 — String 간 충돌. 조회는 byModuleSeq 사용 */
    byModuleId: new Map(),
    byModuleSeq: new Map(),
    moduleCount: 0
  };
}

function ensureStringBucket(index, stringSeq) {
  let bucket = index.byStringSeq.get(stringSeq);
  if (!bucket) {
    bucket = { modules: [], strings: [] };
    index.byStringSeq.set(stringSeq, bucket);
  }
  return bucket;
}

function pushArrayFeature(index, arrayId, feature) {
  let list = index.byArrayId.get(arrayId);
  if (!list) {
    list = [];
    index.byArrayId.set(arrayId, list);
  }
  list.push(feature);
}

export function rebuildPanelMapFeatureIndex(index, features) {
  index.byStringSeq.clear();
  index.byArrayId.clear();
  index.byModuleId.clear();
  index.byModuleSeq.clear();
  let moduleCount = 0;

  for (const feature of features) {
    const featureType = feature.get('featureType');
    const name = feature.get('name') || '';
    const arrayId = readArraySeq(feature);
    const isModule = featureType === 'Module' || featureType === 'module' || name.startsWith('Module');
    const isString = featureType === 'String' || featureType === 'string' || name.startsWith('String');

    if (isModule) {
      moduleCount += 1;
      const moduleDisplayNo = readModuleDisplayNo(feature);
      const moduleSeq = readModuleSeq(feature);
      if (moduleDisplayNo) index.byModuleId.set(moduleDisplayNo, feature);
      if (moduleSeq) index.byModuleSeq.set(moduleSeq, feature);
      const stringSeq = readStringSeq(feature);
      if (stringSeq != null) {
        ensureStringBucket(index, stringSeq).modules.push(feature);
      }
      if (arrayId) pushArrayFeature(index, arrayId, feature);
    } else if (isString) {
      const stringSeq = readStringSeq(feature);
      if (stringSeq != null) {
        ensureStringBucket(index, stringSeq).strings.push(feature);
      }
      if (arrayId) pushArrayFeature(index, arrayId, feature);
    }
  }

  index.moduleCount = moduleCount;
  return index;
}

export function getPanelMapFeatureForModule(index, moduleId) {
  if (!index || !moduleId) return null;
  return index.byModuleId.get(moduleId.toString()) || null;
}

/** Module 조회 — DB seq 기준 (표시 번호 충돌 방지) */
export function getPanelMapFeatureForModuleSeq(index, moduleSeq) {
  if (!index || moduleSeq == null || moduleSeq === '') return null;
  return index.byModuleSeq.get(String(moduleSeq)) || null;
}

/** stringSeq 기준으로 String+Module 피처 조회 */
export function getPanelMapFeaturesForString(index, stringSeq) {
  if (!index || stringSeq == null) return [];
  const bucket = index.byStringSeq.get(String(stringSeq));
  if (!bucket) return [];
  return [...bucket.strings, ...bucket.modules];
}

function isStringAnchorFeature(feature) {
  if (!feature?.get) return false;
  const featureType = (feature.get('featureType') || '').toLowerCase();
  const name = feature.get('name') || '';
  return featureType === 'string' || name.startsWith('String');
}

export function getPanelMapStringAnchorsForString(index, stringSeq) {
  if (!index || stringSeq == null) return [];
  const bucket = index.byStringSeq.get(String(stringSeq));
  if (!bucket?.strings?.length) return [];
  return [...bucket.strings];
}

export function getPanelMapStringAnchorForString(index, stringSeq) {
  const anchors = getPanelMapStringAnchorsForString(index, stringSeq);
  if (anchors[0]) return anchors[0];
  const bucket = index?.byStringSeq?.get(String(stringSeq));
  return bucket?.modules?.[0] || null;
}

export function getPanelMapStringAnchorsForArray(index, arrayId) {
  if (!index || !arrayId) return [];
  const list = index.byArrayId.get(arrayId.toString()) || [];
  const seen = new Set();
  const anchors = [];
  for (const feature of list) {
    if (!isStringAnchorFeature(feature) || seen.has(feature)) continue;
    seen.add(feature);
    anchors.push(feature);
  }
  return anchors;
}

export function getPanelMapFeaturesForArray(index, arrayId) {
  if (!index || !arrayId) return [];
  return index.byArrayId.get(arrayId.toString()) || [];
}
